import { useTranslation } from 'react-i18next';
import { Option, PostDetailStatistics } from '@anytag/shared-types';
import { cn, formatPercent, bigIntFormatter, convertDurationToHhMmSs } from '@anytag/shared-utils';

export interface StatisticsProps {
  className?: string;
  statistics: PostDetailStatistics;
}

export const Statistics = ({ className, statistics }: StatisticsProps) => {
  const { t } = useTranslation();

  const availableStatistics = (() => {
    const keys = Object.keys(statistics) as (keyof PostDetailStatistics)[];
    const {
      likes,
      reach,
      views,
      exits,
      saves,
      shares,
      replies,
      dislikes,
      tapsBack,
      comments,
      retweets,
      reactions,
      engagement,
      quoteTweet,
      tapsForward,
      collections,
      impressions,
      swipeForward,
      averageViewDuration,
      averageViewPercentage
    } = statistics;
    const allItems: Record<keyof PostDetailStatistics, Option<string, string>> = {
      views: { label: t<string>('General.Views'), value: bigIntFormatter(views, 0) },
      saves: { label: t<string>('General.Save'), value: bigIntFormatter(saves, 0) },
      exits: { label: t<string>('General.Exits'), value: bigIntFormatter(exits, 0) },
      likes: { label: t<string>('General.Likes'), value: bigIntFormatter(likes, 0) },
      reach: { label: t<string>('General.Reach'), value: bigIntFormatter(reach, 0) },
      dislikes: { label: t<string>('Dislikes'), value: bigIntFormatter(dislikes, 0) },
      shares: { label: t<string>('General.Shares'), value: bigIntFormatter(shares, 0) },
      replies: { label: t<string>('General.Replies'), value: bigIntFormatter(replies, 0) },
      tapsBack: { label: t<string>('General.Tap back'), value: bigIntFormatter(tapsBack, 0) },
      collections: { label: t<string>('General.Collections'), value: bigIntFormatter(collections, 0) },
      retweets: { label: t<string>('General.Retweets'), value: bigIntFormatter(retweets, 0) },
      comments: { label: t<string>('General.Comments'), value: bigIntFormatter(comments, 0) },
      impressions: { label: t<string>('General.Imps'), value: bigIntFormatter(impressions, 0) },
      reactions: { label: t<string>('General.Reactions'), value: bigIntFormatter(reactions, 0) },
      engagement: { label: t<string>('General.Engagement'), value: bigIntFormatter(engagement, 0) },
      quoteTweet: { label: t<string>('General.Quote Tweet'), value: bigIntFormatter(quoteTweet, 0) },
      tapsForward: { label: t<string>('General.Tapforward'), value: bigIntFormatter(tapsForward, 0) },
      swipeForward: { label: t<string>('General.Swipeforward'), value: bigIntFormatter(swipeForward, 0) },
      averageViewDuration: {
        label: t<string>('General.Average View Duration'),
        value: convertDurationToHhMmSs(averageViewDuration ?? null)
      },
      averageViewPercentage: {
        value: formatPercent(averageViewPercentage),
        label: t<string>('General.Average View Duration Rate')
      }
    };

    return keys.reduce((rs, key) => [...rs, allItems[key]], [] as Option<string, string>[]);
  })();

  return (
    <div className={cn('flex w-full gap-5', className)}>
      <div className="flex-1">
        <div className="-ml-2 flex items-start">
          <div className="flex w-full flex-wrap gap-y-3">
            {availableStatistics.map((item, index) => (
              <div key={index} className="px-2">
                <p className="text-gray-lv3 text-sm leading-none font-normal whitespace-nowrap text-zinc-500">
                  {item.label}
                </p>
                <p className="mt-1 text-lg leading-none font-semibold text-black-90" title={item.value}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
